import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, FileText, Map, AlertTriangle, ClipboardList } from "lucide-react";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const inputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement.tagName !== "INPUT" && document.activeElement.tagName !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/buscar", { params: { q } });
      setRes(data);
      setOpen(true);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const total = res ? res.documentos.length + res.requisitos.length + res.gaps.length + res.incidencias.length : 0;

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <div className="flex items-center gap-2 bg-[#F7F8FA] border border-[#E2E8F0] rounded-md px-3 py-1.5">
        <Search size={15} className="text-[#0E2A47]/40" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          data-testid="global-search-input"
          placeholder="Buscar…  (pulsa / para enfocar)"
          className="bg-transparent outline-none text-sm w-full text-[#0E2A47] placeholder:text-[#0E2A47]/40"
        />
      </div>
      {open && res && (
        <div className="absolute top-11 left-0 right-0 bg-white border border-[#E2E8F0] rounded-md shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="global-search-results">
          {total === 0 && <div className="p-4 text-sm text-[#0E2A47]/50">Sin resultados.</div>}
          {res.documentos.map((d) => (
            <button key={d.id} onClick={() => go("/documentos")} className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-2 text-sm">
              <FileText size={14} className="text-[#0E2A47]/50" /> <span className="font-mono text-xs text-[#FF5625]">{d.codigo}</span> {d.titulo}
            </button>
          ))}
          {res.requisitos.map((r) => (
            <button key={r.codigo} onClick={() => go("/norma")} className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-2 text-sm">
              <Map size={14} className="text-[#0E2A47]/50" /> <span className="font-mono text-xs text-[#FF5625]">{r.codigo}</span> {r.texto}
            </button>
          ))}
          {res.gaps.map((g) => (
            <button key={g.id} onClick={() => go("/gaps")} className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-2 text-sm">
              <AlertTriangle size={14} className="text-[#0E2A47]/50" /> <span className="uppercase text-xs font-bold">{g.tipo}</span> {g.descripcion?.slice(0, 50)}
            </button>
          ))}
          {res.incidencias.map((i) => (
            <button key={i.id} onClick={() => go("/incidencias")} className="w-full text-left px-4 py-2 hover:bg-[#F7F8FA] flex items-center gap-2 text-sm">
              <ClipboardList size={14} className="text-[#0E2A47]/50" /> {i.descripcion?.slice(0, 50)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
